import { flow } from 'fp-ts/function';
import { DateFormat } from '@/constants/formatting';
import { type ValueOf } from '@/types';
import {
	addWeekdayToFront,
	applyDateFnsFormat,
	extractDate,
	extractDateAndTime,
	type FormatterPayload,
	override,
	removeYearIfCurrent,
	replaceDateWithToday,
} from './formatterUtils';

/**
 * Date (and time) formatting is done by composing formatter functions
 * together.
 *
 * Each format should be specified in this object where the key is the format
 * name and the value is a function which converts the given payload to the
 * final formatted date(time).
 *
 * The input for each formatter is a `FormatterPayload` with the date-fns
 * compatible formats. The output for a formatter should be the final formatted
 * date as a string.
 *
 * To add a new format:
 * 1. Add the format to the constants in `constants/formatting.ts`
 * 2. Add a new field to this `formatters` object
 * 3. Add any missing utility functions that can be composed together with
 *    others
 * 4. Use `pipe` to compose the formatter functions
 * 5. Return a helper function from `useFormatter` with the new format
 */
export const formatters: Record<
	ValueOf<typeof DateFormat>,
	(payload: FormatterPayload) => string
> = {
	[DateFormat.DATE_SHORT_NO_CURRENT_YEAR]: flow(
		removeYearIfCurrent,
		addWeekdayToFront,
		applyDateFnsFormat,
		extractDate
	),
	[DateFormat.DATE_NO_CURRENT_YEAR]: flow(
		removeYearIfCurrent,
		applyDateFnsFormat,
		extractDate
	),
	[DateFormat.DATE]: flow(applyDateFnsFormat, extractDate),
	[DateFormat.DATE_TIME_FULL]: flow(applyDateFnsFormat, extractDateAndTime),
	[DateFormat.DATE_RELATIVE_TO_TODAY_WITH_TIME]: flow(
		applyDateFnsFormat,
		replaceDateWithToday,
		extractDateAndTime
	),
	[DateFormat.ONLY_MONTH_AS_WORD]: flow(
		override({ dateFormat: 'MMMM' }),
		applyDateFnsFormat,
		extractDate
	),
	[DateFormat.ONLY_DAY_NUMBER]: flow(
		override({ dateFormat: 'd' }),
		applyDateFnsFormat,
		extractDate
	),
	[DateFormat.ONLY_YEAR_NUMBER]: flow(
		override({ dateFormat: 'yyyy' }),
		applyDateFnsFormat,
		extractDate
	),
	[DateFormat.ONLY_WEEK_OF_YEAR]: flow(
		override({ dateFormat: 'II' }),
		applyDateFnsFormat,
		extractDate
	),
	[DateFormat.MONTH_SHORT_NO_CURRENT_YEAR]: flow(
		override({ dateFormat: 'MMM yyyy' }),
		removeYearIfCurrent,
		applyDateFnsFormat,
		extractDate
	),
};
